import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Calendar, MapPin, Users, Plus, Trash2, Edit2, X, Search, 
    RefreshCw, Clock, ChevronRight, Sparkles, Brain, Send, 
    ArrowRight, Check, Download, UserPlus, QrCode, Maximize, 
    Smartphone, FileText, ArrowLeft, Ticket
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useDevice } from '../hooks/useDevice';
import BrowserQrScanner from '../components/BrowserQrScanner';

const EventCard = ({ event, onManage, onEdit, onDelete, canManage, navigate }) => (
    <div 
        onClick={() => navigate(`/events/${event.id}/checkin`)}
        className="premium-card bg-neutral-900 border border-neutral-800 rounded-[2.5rem] md:rounded-[3.5rem] p-4 md:p-6 shadow-2xl hover:border-blue-500/50 transition-all flex flex-col min-h-[380px] md:min-h-[450px] group cursor-pointer italic"
    >
        <div className="relative h-44 md:h-56 rounded-[2rem] md:rounded-[2.5rem] overflow-hidden mb-6 bg-neutral-950 shadow-inner">
            {event.image_url ? (
                <img src={event.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={event.title} />
            ) : (
                <div className="w-full h-full flex items-center justify-center opacity-10">
                    <Calendar size={64} />
                </div>
            )}
            <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-4 py-2 rounded-full text-[8px] md:text-[9px] font-black text-blue-500 uppercase tracking-widest border border-white/5">
                {event.bot_name || 'System Bot'}
            </div>
            <div className={`absolute bottom-4 left-4 px-3 py-1.5 rounded-xl text-[8px] font-black uppercase tracking-widest border ${new Date(event.event_date) > new Date() ? 'bg-emerald-500/80 text-white border-emerald-400/20 shadow-lg' : 'bg-neutral-900/80 text-neutral-500 border-neutral-800'}`}>
                {new Date(event.event_date) > new Date() ? 'Вход открыт' : 'Завершено'}
            </div>
        </div>
        
        <div className="px-2 space-y-4 flex-1">
            <h3 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter leading-tight line-clamp-2">{event.title}</h3>
            <div className="space-y-2">
                <div className="flex items-center gap-3 text-neutral-500 text-[10px] md:text-[11px] font-bold uppercase tracking-wide">
                    <Clock size={14} className="text-blue-500/40" /> 
                    {new Date(event.event_date).toLocaleString('ru-RU', { day:'2-digit', month:'long', hour:'2-digit', minute:'2-digit' })}
                </div>
                <div className="flex items-center gap-3 text-neutral-500 text-[10px] md:text-[11px] font-bold uppercase tracking-wide">
                    <MapPin size={14} className="text-blue-500/40" /> 
                    <span className="truncate">{event.location || 'Локация не указана'}</span>
                </div>
            </div>
        </div>

        <div className="p-2 pt-6 flex items-center justify-between border-t border-white/5">
            <div className="text-[10px] md:text-xs font-black text-white italic">
                {event.rsvp_count || 0} <span className="text-neutral-600 font-normal uppercase tracking-widest">рекуррентов</span>
            </div>
            <div className="flex items-center gap-2">
                {canManage && (
                    <>
                        <button 
                            onClick={(e) => { e.stopPropagation(); onManage(event); }}
                            className="p-3 bg-neutral-800 hover:bg-white hover:text-black rounded-xl md:rounded-2xl text-neutral-400 transition-all border border-neutral-700 shadow-xl"
                        >
                            <Maximize size={16} />
                        </button>
                        <div className="hidden md:flex gap-2">
                            <button 
                                onClick={(e) => { e.stopPropagation(); onEdit(event); }} 
                                className="w-12 h-12 bg-neutral-800 rounded-xl flex items-center justify-center text-neutral-400 hover:text-white transition-all border border-neutral-700 shadow-xl"
                            >
                                <Edit2 size={16}/>
                            </button>
                            <button 
                                onClick={(e) => { e.stopPropagation(); onDelete(event.id); }} 
                                className="w-12 h-12 bg-rose-500/5 rounded-xl flex items-center justify-center text-rose-500 hover:bg-rose-500 hover:text-white transition-all border border-rose-500/10 shadow-xl"
                            >
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    </>
                )}
                <div className="md:hidden text-[10px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-1 ml-2">Чекин <ChevronRight size={14} /></div>
            </div>
        </div>
    </div>
);

const toLocalISOString = (dateOrStr) => {
    if (!dateOrStr) return '';
    const date = new Date(dateOrStr);
    if (isNaN(date.getTime())) return '';
    const tzOffset = date.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(date.getTime() - tzOffset)).toISOString().slice(0, 16);
    return localISOTime;
};

const Events = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isMobile, isTelegram } = useDevice();
    
    // UI State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRSVPModalOpen, setIsRSVPModalOpen] = useState(false);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannedParticipant, setScannedParticipant] = useState(null);
    const [isManualRSVPOpen, setIsManualRSVPOpen] = useState(false);
    const [manualRSVPData, setManualRSVPData] = useState({ first_name: '', last_name: '', phone: '', tariff: 'Стандарт', final_price: 0 });
    const [isLibraryOpen, setIsLibraryOpen] = useState(false);
    const [libraryTarget, setLibraryTarget] = useState(null); // 'image_url' or 'logo_url' or 'ticket_bg_url'
    const [libraryFiles, setLibraryFiles] = useState([]);
    const [peopleSearch, setPeopleSearch] = useState('');
    const [peopleResults, setPeopleResults] = useState([]);
    const [isSearchingPeople, setIsSearchingPeople] = useState(false);
    
    // Data State
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [rsvpData, setRsvpData] = useState([]);
    const [loadingRSVPs, setLoadingRSVPs] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [bots, setBots] = useState([]);
    const [editingRsvpId, setEditingRsvpId] = useState(null);
    const [editRsvpData, setEditRsvpData] = useState({ first_name: '', last_name: '', phone: '' });

    const [selectedTemplate, setSelectedTemplate] = useState('');
    const canManageEvents = ['admin', 'superadmin', 'rop', 'partner'].includes(user?.role);
    
    const initialFormData = {
        title: '', description: '', event_date: '', location: '', image_url: '',
        max_participants: '', ticket_template: '', base_price: 0, ticket_bg_url: '',
        video_url: '', map_link: '', parking_info: '', entry_info: '', product_id: '', logo_url: '',
        bot_id: '', ticket_pdf_template_url: '', ticket_pdf_y_offset: 50
    };

    const [formData, setFormData] = useState(initialFormData);

    // Fetch Events
    const { data: events = [], isLoading, refetch } = useQuery({
        queryKey: ['events'],
        queryFn: async () => (await client.get('/events')).data
    });

    useEffect(() => {
        const fetchBots = async () => {
            try {
                const res = await client.get('/bots');
                setBots(res.data);
            } catch (err) { console.error("Error fetching bots", err); }
        };
        fetchBots();
    }, []);

    const { data: products = [] } = useQuery({
        queryKey: ['products'],
        queryFn: async () => (await client.get('/products')).data
    });

    const createMutation = useMutation({
        mutationFn: (data) => client.post('/events', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['events']);
            toast.success('Мероприятие создано');
            setIsModalOpen(false);
            setFormData(initialFormData);
        },
        onError: (err) => {
            console.error("Create event error:", err);
            toast.error(err.response?.data?.detail || 'Ошибка при создании мероприятия');
        }
    });

    const updateMutation = useMutation({
        mutationFn: (data) => client.patch(`/events/${selectedEvent.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['events']);
            toast.success('Обновлено');
            setIsModalOpen(false);
            setSelectedEvent(null);
        },
        onError: (err) => {
            console.error("Update event error:", err);
            toast.error(err.response?.data?.detail || 'Ошибка при обновлении мероприятия');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/events/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['events']);
            toast.success('Удалено');
        }
    });

    const handleManualRSVP = async (e) => {
        e.preventDefault();
        try {
            await client.post(`/events/${selectedEvent.id}/rsvps/manual`, manualRSVPData);
            toast.success('Участник добавлен и билет выпущен');
            setIsManualRSVPOpen(false);
            setManualRSVPData({ first_name: '', last_name: '', phone: '', tariff: 'Стандарт', final_price: 0 });
            viewRSVPs(selectedEvent);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Ошибка добавления');
        }
    };

    const searchPeople = async (query) => {
        if (!query || query.length < 2) {
            setPeopleResults([]);
            return;
        }
        setIsSearchingPeople(true);
        try {
            const res = await client.get('/crm/search', { params: { query } });
            setPeopleResults(res.data);
        } catch (err) {
            toast.error('Ошибка поиска людей');
        } finally {
            setIsSearchingPeople(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (peopleSearch) searchPeople(peopleSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [peopleSearch]);

    const handleSelectPerson = (person) => {
        const names = person.name.split(' ');
        setManualRSVPData({
            ...manualRSVPData,
            first_name: names[0] || '',
            last_name: names.slice(1).join(' ') || '',
            phone: person.phone || ''
        });
        setPeopleSearch('');
        setPeopleResults([]);
    };

    const fetchLibrary = async () => {
        try {
            const res = await client.get('/uploads');
            setLibraryFiles(res.data);
            setIsLibraryOpen(true);
        } catch (err) {
            toast.error('Ошибка загрузки библиотеки');
        }
    };

    const handleFileUpload = async (e, target) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await client.post('/uploads', formData);
            setFormData(prev => ({ ...prev, [target]: res.data.url }));
            toast.success('Файл загружен');
        } catch (err) {
            toast.error('Ошибка загрузки');
        }
    };

    const viewRSVPs = async (event) => {
        setSelectedEvent(event);
        setIsRSVPModalOpen(true);
        setLoadingRSVPs(true);
        try {
            setRsvpData((await client.get(`/events/${event.id}/rsvps`)).data);
        } catch { toast.error('Ошибка загрузки данных'); }
        finally { setLoadingRSVPs(false); }
    };

    const handleSaveRsvp = async (rsvpId) => {
        try {
            await client.patch(`/events/rsvps/${rsvpId}`, editRsvpData);
            toast.success('Данные билета обновлены');
            setEditingRsvpId(null);
            // Refresh RSVPs list
            const res = await client.get(`/events/${selectedEvent.id}/rsvps`);
            setRsvpData(res.data);
        } catch {
            toast.error('Ошибка сохранения данных');
        }
    };

    const handleDeleteRsvp = async (rsvpId) => {
        const confirmMsg = 'Вы уверены, что хотите удалить этот билет?';
        const performDelete = async () => {
            try {
                await client.delete(`/events/rsvps/${rsvpId}`);
                toast.success('Билет удален');
                // Refresh RSVPs list
                const res = await client.get(`/events/${selectedEvent.id}/rsvps`);
                setRsvpData(res.data);
            } catch {
                toast.error('Ошибка удаления билета');
            }
        };

        const tg = window.Telegram?.WebApp;
        const isTelegram = !!tg?.initData;
        if (isTelegram && tg?.showConfirm) {
            tg.showConfirm(confirmMsg, (ok) => {
                if (ok) performDelete();
            });
        } else {
            if (window.confirm(confirmMsg)) {
                performDelete();
            }
        }
    };

    const handleEdit = (event) => {
        setSelectedEvent(event);
        setFormData({
            ...event,
            event_date: toLocalISOString(event.event_date)
        });
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
            bot_id: formData.bot_id ? parseInt(formData.bot_id) : null,
            base_price: parseFloat(formData.base_price) || 0,
            product_id: formData.product_id ? parseInt(formData.product_id) : null,
            ticket_pdf_y_offset: formData.ticket_pdf_y_offset ? parseInt(formData.ticket_pdf_y_offset) : 50
        };
        if (selectedEvent) updateMutation.mutate(payload);
        else createMutation.mutate(payload);
    };

    const handleQuickScan = () => {
        const tg = window.Telegram?.WebApp;
        if (tg?.showScanQrPopup) {
            tg.showScanQrPopup({ text: 'Сканируйте QR-код билета' }, async (code) => {
                if (!code) return false;
                try {
                    const res = await client.post('/events/check-in', { ticket_code: code.trim() });
                    setScannedParticipant(res.data);
                    refetch();
                    setTimeout(() => tg.closeScanQrPopup(), 100);
                    return true;
                } catch (err) { 
                    toast.error(err.response?.data?.detail || 'Ошибка сканирования'); 
                    return false; 
                }
            });
        } else {
            setIsScannerOpen(true);
        }
    };

    const filteredEvents = events.filter(e => 
        e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.location && e.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8">
                <div className="flex items-center gap-6">
                    <button onClick={() => navigate('/')} className="md:hidden p-3 bg-neutral-900 border border-neutral-800 rounded-2xl text-neutral-400 hover:text-white transition-all shadow-xl">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="hidden md:flex w-20 h-20 bg-blue-600 rounded-[2rem] items-center justify-center shadow-2xl rotate-3 shrink-0">
                        <Calendar size={40} className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">События</h1>
                        <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">ERP Жизненный цикл :: Lifecycle</p>
                    </div>
                </div>
                
                <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl md:rounded-3xl p-1.5 flex gap-2 w-full md:w-auto shadow-2xl">
                        <div className="relative flex-1 md:w-64">
                             <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
                             <input 
                                type="text" 
                                placeholder="Найти лот..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-neutral-950 border border-neutral-800 rounded-xl md:rounded-2xl py-3 pl-11 text-[10px] md:text-xs text-white font-bold outline-none focus:border-blue-500 italic" 
                             />
                        </div>
                        <button onClick={handleQuickScan} className="bg-blue-600 text-white p-3 md:p-4 rounded-xl md:rounded-2xl hover:bg-blue-500 shadow-xl transition-all">
                            <QrCode size={20}/>
                        </button>
                        <button onClick={() => refetch()} className="hidden md:flex p-4 bg-neutral-800 text-neutral-500 rounded-2xl hover:text-white transition-all">
                            <RefreshCw className={isLoading ? 'animate-spin' : ''} size={20}/>
                        </button>
                    </div>
                    {canManageEvents && (
                        <button 
                            onClick={() => { setSelectedEvent(null); setFormData(initialFormData); setIsModalOpen(true); }} 
                            className="w-full md:w-auto bg-white text-black px-8 py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-xl flex items-center justify-center gap-3 italic"
                        >
                            <Plus size={20}/> {isMobile ? 'Создать' : 'Создать событие'}
                        </button>
                    )}
                </div>
            </header>

            {/* Responsive Grid Content */}
            <div className="relative min-h-[400px]">
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3].map(i => <div key={i} className="h-[400px] bg-neutral-900/50 rounded-[3.5rem] border border-neutral-800 animate-pulse" />)}
                    </div>
                ) : filteredEvents.length === 0 ? (
                    <div className="py-24 text-center opacity-20 flex flex-col items-center gap-6">
                        <Calendar size={64} />
                        <p className="text-sm font-black uppercase tracking-[0.4em]">Мероприятия не запланированы</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
                        {filteredEvents.map(event => (
                            <EventCard 
                                key={event.id} 
                                event={event} 
                                onManage={viewRSVPs} 
                                onEdit={handleEdit} 
                                onDelete={(id) => { if(window.confirm('Удалить событие?')) deleteMutation.mutate(id); }}
                                canManage={canManageEvents}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modals Section */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/95 backdrop-blur-2xl" onClick={() => setIsModalOpen(false)} />
                    <div className="relative w-full max-w-2xl bg-neutral-900 border border-neutral-800 rounded-[3rem] md:rounded-[4rem] p-8 md:p-12 shadow-3xl flex flex-col max-h-[90vh] italic overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-8 relative z-10">
                             <div>
                                <h2 className="text-2xl md:text-3xl font-black text-white italic uppercase tracking-tighter leading-none">
                                    {selectedEvent ? 'Настройка Ивента' : 'Новое Событие'}
                                </h2>
                                <p className="text-[9px] md:text-[10px] text-blue-500 font-bold uppercase tracking-widest mt-2 italic px-1">Million Connections :: Event Lifecycle</p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="w-12 h-12 md:w-16 md:h-16 bg-neutral-800 rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="overflow-y-auto custom-scrollbar space-y-10 pr-2 text-left relative z-10 pb-8">
                            
                            {/* SECTION: BASIC INFO */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-1 h-4 bg-blue-600 rounded-full" />
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-white">Основная информация</h4>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {!selectedEvent && events.length > 0 && (
                                        <div className="col-span-full">
                                            <label className="text-[9px] font-black text-blue-500 uppercase tracking-widest px-2 mb-2 block italic">Создать по шаблону (скопировать данные)</label>
                                            <select 
                                                value={selectedTemplate} 
                                                onChange={e => {
                                                    const templateEvent = events.find(ev => ev.id === parseInt(e.target.value));
                                                    setSelectedTemplate(e.target.value);
                                                    if (templateEvent) {
                                                        setFormData({
                                                            title: templateEvent.title || '',
                                                            description: templateEvent.description || '',
                                                            event_date: toLocalISOString(templateEvent.event_date),
                                                            location: templateEvent.location || '',
                                                            map_link: templateEvent.map_link || '',
                                                            video_url: templateEvent.video_url || '',
                                                            parking_info: templateEvent.parking_info || '',
                                                            entry_info: templateEvent.entry_info || '',
                                                            base_price: templateEvent.base_price || 0,
                                                            max_participants: templateEvent.max_participants || 100,
                                                            product_id: templateEvent.product_id || '',
                                                            image_url: templateEvent.image_url || '',
                                                            logo_url: templateEvent.logo_url || '',
                                                            bot_id: templateEvent.bot_id || '',
                                                            ticket_pdf_template_url: templateEvent.ticket_pdf_template_url || '',
                                                            ticket_pdf_y_offset: templateEvent.ticket_pdf_y_offset || 50
                                                        });
                                                        toast.success('Данные скопированы из шаблона');
                                                    }
                                                }}
                                                className="w-full bg-neutral-950 border border-blue-900/50 rounded-2xl px-6 py-4 text-xs text-white font-bold outline-none focus:border-blue-500 transition-all italic"
                                            >
                                                <option value="">-- Выберите мероприятие для копирования --</option>
                                                {events.map(ev => (
                                                    <option key={ev.id} value={ev.id}>
                                                        {ev.title} ({ev.event_date ? new Date(ev.event_date).toLocaleDateString('ru-RU') : ''})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="col-span-full">
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Название мероприятия</label>
                                        <input required type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl md:rounded-[2rem] px-6 py-4 md:py-5 text-white font-black md:text-xl outline-none focus:border-blue-500 transition-all italic" />
                                    </div>
                                    <div className="col-span-full">
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Описание</label>
                                        <textarea rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-medium outline-none focus:border-blue-500 transition-all italic resize-none" placeholder="О чем это событие?" />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Дата и время начала</label>
                                        <input required type="datetime-local" value={formData.event_date} onChange={e => setFormData({...formData, event_date: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-bold outline-none focus:border-blue-500 transition-all font-mono" />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Лимит участников</label>
                                        <input type="number" value={formData.max_participants} onChange={e => setFormData({...formData, max_participants: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-bold outline-none focus:border-blue-500 transition-all" placeholder="Безлимитно" />
                                    </div>
                                </div>
                            </div>

                            {/* SECTION: LOGISTICS */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-1 h-4 bg-emerald-600 rounded-full" />
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-white">Локация и Навигация</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-full">
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Место проведения</label>
                                        <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-bold outline-none focus:border-blue-500 transition-all italic" placeholder="г. Ташкент, ул. ..." />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Ссылка на карту (Google/Yandex)</label>
                                        <input type="text" value={formData.map_link} onChange={e => setFormData({...formData, map_link: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-zinc-400 font-bold outline-none focus:border-blue-500 transition-all" placeholder="https://maps.app.goo.gl/..." />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Видео-инструкция (URL)</label>
                                        <input type="text" value={formData.video_url} onChange={e => setFormData({...formData, video_url: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-zinc-400 font-bold outline-none focus:border-blue-500 transition-all" placeholder="https://t.me/..." />
                                    </div>
                                    <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Инфо о парковке</label>
                                            <textarea rows={2} value={formData.parking_info} onChange={e => setFormData({...formData, parking_info: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-medium outline-none focus:border-blue-500 transition-all italic resize-none" placeholder="Где оставить машину?" />
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Инструкция по входу</label>
                                            <textarea rows={2} value={formData.entry_info} onChange={e => setFormData({...formData, entry_info: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-medium outline-none focus:border-blue-500 transition-all italic resize-none" placeholder="Куда идти от входа?" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SECTION: MEDIA & TICKETS */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-1 h-4 bg-amber-600 rounded-full" />
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-white">Оформление и Билеты</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div>
                                         <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Обложка (URL)</label>
                                         <div className="flex gap-2">
                                             <input type="text" value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} className="flex-1 bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-zinc-500 font-bold outline-none focus:border-blue-500 transition-all" placeholder="Баннер мероприятия" />
                                             <label className="p-4 bg-neutral-800 rounded-2xl cursor-pointer hover:bg-neutral-700 transition-all text-neutral-400"><Plus size={16}/><input type="file" className="hidden" onChange={e => handleFileUpload(e, 'image_url')} /></label>
                                             <button type="button" onClick={() => { setLibraryTarget('image_url'); fetchLibrary(); }} className="p-4 bg-neutral-800 rounded-2xl hover:bg-neutral-700 transition-all text-neutral-400"><FileText size={16}/></button>
                                         </div>
                                     </div>
                                     <div>
                                         <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Логотип (URL)</label>
                                         <div className="flex gap-2">
                                             <input type="text" value={formData.logo_url} onChange={e => setFormData({...formData, logo_url: e.target.value})} className="flex-1 bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-zinc-500 font-bold outline-none focus:border-blue-500 transition-all" placeholder="Мини-логотип" />
                                             <label className="p-4 bg-neutral-800 rounded-2xl cursor-pointer hover:bg-neutral-700 transition-all text-neutral-400"><Plus size={16}/><input type="file" className="hidden" onChange={e => handleFileUpload(e, 'logo_url')} /></label>
                                             <button type="button" onClick={() => { setLibraryTarget('logo_url'); fetchLibrary(); }} className="p-4 bg-neutral-800 rounded-2xl hover:bg-neutral-700 transition-all text-neutral-400"><FileText size={16}/></button>
                                         </div>
                                     </div>
                                     <div className="col-span-full">
                                         <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Фон билета (URL)</label>
                                         <div className="flex gap-2">
                                             <input type="text" value={formData.ticket_bg_url} onChange={e => setFormData({...formData, ticket_bg_url: e.target.value})} className="flex-1 bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-zinc-500 font-bold outline-none focus:border-blue-500 transition-all" placeholder="Стильный фон для билета" />
                                             <label className="p-4 bg-neutral-800 rounded-2xl cursor-pointer hover:bg-neutral-700 transition-all text-neutral-400"><Plus size={16}/><input type="file" className="hidden" onChange={e => handleFileUpload(e, 'ticket_bg_url')} /></label>
                                             <button type="button" onClick={() => { setLibraryTarget('ticket_bg_url'); fetchLibrary(); }} className="p-4 bg-neutral-800 rounded-2xl hover:bg-neutral-700 transition-all text-neutral-400"><FileText size={16}/></button>
                                         </div>
                                     </div>
                                     <div className="col-span-full border-t border-white/5 pt-6 mt-2">
                                         <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">PDF Шаблон билета (Сложный дизайн)</label>
                                         <div className="flex gap-2">
                                             <input type="text" value={formData.ticket_pdf_template_url} onChange={e => setFormData({...formData, ticket_pdf_template_url: e.target.value})} className="flex-1 bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-zinc-500 font-bold outline-none focus:border-blue-500 transition-all" placeholder="Ссылка на PDF файл" />
                                             <label className="p-4 bg-neutral-800 rounded-2xl cursor-pointer hover:bg-neutral-700 transition-all text-neutral-400"><Plus size={16}/><input type="file" className="hidden" onChange={e => handleFileUpload(e, 'ticket_pdf_template_url')} /></label>
                                         </div>
                                     </div>
                                     <div className="col-span-full">
                                         <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Вертикальный отступ данных в PDF (мм)</label>
                                         <input type="number" value={formData.ticket_pdf_y_offset} onChange={e => setFormData({...formData, ticket_pdf_y_offset: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-bold outline-none focus:border-blue-500 transition-all" placeholder="Например: 50" />
                                     </div>
                                </div>
                            </div>

                            {/* SECTION: AUTOMATION */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-1 h-4 bg-violet-600 rounded-full" />
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-white">Автоматизация и Продажи</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Управляющий Бот</label>
                                        <select required value={formData.bot_id} onChange={e => setFormData({...formData, bot_id: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-bold outline-none focus:border-blue-500 transition-all">
                                            <option value="">Выберите бота...</option>
                                            {bots.map(b => (
                                                <option key={b.id} value={b.id}>{b.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Связанный продукт (ERP)</label>
                                        <select value={formData.product_id} onChange={e => setFormData({...formData, product_id: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-xs md:text-sm text-white font-bold outline-none focus:border-blue-500 transition-all">
                                            <option value="">Не привязан</option>
                                            {products.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Базовая цена</label>
                                        <input type="number" value={formData.base_price} onChange={e => setFormData({...formData, base_price: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4.5 text-white font-bold outline-none focus:border-blue-500 transition-all" />
                                    </div>
                                </div>
                            </div>

                            <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-[2rem] md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-[0.2em] shadow-2xl shadow-blue-500/20 active:scale-95 transition-all mt-4 italic">
                                {createMutation.isPending || updateMutation.isPending ? 'Синхронизация...' : 'Развернуть мероприятие'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* RSVP Management Drawer */}
            {isRSVPModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-end md:items-stretch md:justify-end italic">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setIsRSVPModalOpen(false)} />
                    <div className="relative w-full md:w-[600px] bg-neutral-950 border-t md:border-t-0 md:border-l border-neutral-800 rounded-t-[3rem] md:rounded-none p-8 md:p-12 shadow-3xl flex flex-col h-[90vh] md:h-screen animate-in slide-in-from-right-4 duration-500">
                        <div className="flex justify-between items-center mb-6">
                             <div>
                                 <h2 className="text-2xl md:text-3xl font-black text-white italic uppercase tracking-tighter leading-none">Управление RSVP</h2>
                                 <p className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mt-2 italic truncate max-w-[300px]">{selectedEvent?.title}</p>
                             </div>
                             <div className="flex gap-2">
                                 <button 
                                     onClick={() => setIsManualRSVPOpen(!isManualRSVPOpen)}
                                     className="w-12 h-12 bg-blue-600 rounded-2xl text-white hover:bg-blue-500 transition-all shadow-xl flex items-center justify-center"
                                 >
                                     <UserPlus size={24}/>
                                 </button>
                                 <button onClick={() => setIsRSVPModalOpen(false)} className="w-12 h-12 bg-neutral-900 border border-neutral-800 rounded-2xl text-neutral-500 hover:text-white transition-all shadow-xl flex items-center justify-center"><X size={24}/></button>
                             </div>
                         </div>

                         {isManualRSVPOpen && (
                             <div className="mb-10 bg-neutral-900 border border-neutral-800 rounded-[2rem] p-6 space-y-4 animate-in slide-in-from-top-4 duration-300">
                                 <h3 className="text-xs font-black uppercase tracking-widest text-blue-500 mb-2">Новый участник</h3>
                                 
                                 {/* CRM Search */}
                                 <div className="relative">
                                     <div className="relative group">
                                         <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-600" />
                                         <input 
                                             type="text" 
                                             placeholder="Поиск по CRM (Лиды/Клиенты)..."
                                             value={peopleSearch}
                                             onChange={(e) => setPeopleSearch(e.target.value)}
                                             className="w-full bg-neutral-950 border border-neutral-800 rounded-xl pl-10 pr-4 py-3 text-[10px] text-white focus:border-blue-500 outline-none transition-all"
                                         />
                                         {isSearchingPeople && <Loader2 size={12} className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />}
                                     </div>
                                     
                                     {peopleResults.length > 0 && (
                                         <div className="absolute top-full left-0 right-0 mt-1 bg-neutral-950 border border-neutral-800 rounded-xl overflow-hidden z-[210] shadow-2xl">
                                             {peopleResults.map(person => (
                                                 <button 
                                                     key={`${person.type}-${person.id}`}
                                                     onClick={() => handleSelectPerson(person)}
                                                     className="w-full px-4 py-2 text-left hover:bg-neutral-800 border-b border-neutral-800 last:border-0 flex justify-between items-center group"
                                                 >
                                                     <div>
                                                         <p className="text-[10px] font-black text-white uppercase italic">{person.name}</p>
                                                         <p className="text-[8px] text-neutral-500 font-bold">{person.phone}</p>
                                                     </div>
                                                     <span className={`text-[7px] font-black uppercase px-1.5 py-0.5 rounded ${person.type === 'lead' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                                         {person.type === 'lead' ? 'Лид' : 'Клиент'}
                                                     </span>
                                                 </button>
                                             ))}
                                         </div>
                                     )}
                                 </div>

                                 <form onSubmit={handleManualRSVP} className="space-y-4">
                                     <div className="grid grid-cols-2 gap-4">
                                         <input required placeholder="Имя" value={manualRSVPData.first_name} onChange={e => setManualRSVPData({...manualRSVPData, first_name: e.target.value})} className="bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-3 text-xs text-white outline-none focus:border-blue-500" />
                                         <input placeholder="Фамилия" value={manualRSVPData.last_name} onChange={e => setManualRSVPData({...manualRSVPData, last_name: e.target.value})} className="bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-3 text-xs text-white outline-none focus:border-blue-500" />
                                         <input required placeholder="Телефон" value={manualRSVPData.phone} onChange={e => setManualRSVPData({...manualRSVPData, phone: e.target.value})} className="bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-3 text-xs text-white outline-none focus:border-blue-500" />
                                         <select value={manualRSVPData.tariff} onChange={e => setManualRSVPData({...manualRSVPData, tariff: e.target.value})} className="bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-3 text-xs text-white outline-none focus:border-blue-500">
                                             <option value="Стандарт">Стандарт</option>
                                             <option value="Бизнес">Бизнес</option>
                                             <option value="VIP">VIP</option>
                                             <option value="Резидент">Резидент</option>
                                         </select>
                                     </div>
                                     <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-blue-500 transition-all">Выпустить билет</button>
                                 </form>
                             </div>
                         )}

                         <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-2">
                            {loadingRSVPs ? (
                                <div className="text-center py-40 opacity-50 flex flex-col items-center gap-4">
                                     <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                     <p className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Загрузка аккредитаций...</p>
                                </div>
                            ) : rsvpData.length === 0 ? (
                                <div className="py-20 text-center opacity-10 flex flex-col items-center gap-6">
                                     <Users size={48} />
                                     <p className="text-xs font-black uppercase tracking-widest">Нет участников</p>
                                </div>
                            ) : (
                             rsvpData.map(rsvp => (
                                <div key={rsvp.id} className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-[2rem] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 group active:scale-[0.99] transition-all">
                                    {editingRsvpId === rsvp.id ? (
                                        <div className="flex-1 w-full space-y-3">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                <input 
                                                    type="text" 
                                                    placeholder="Имя"
                                                    value={editRsvpData.first_name} 
                                                    onChange={e => setEditRsvpData({...editRsvpData, first_name: e.target.value})} 
                                                    className="bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-blue-500 w-full" 
                                                />
                                                <input 
                                                    type="text" 
                                                    placeholder="Фамилия"
                                                    value={editRsvpData.last_name} 
                                                    onChange={e => setEditRsvpData({...editRsvpData, last_name: e.target.value})} 
                                                    className="bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-blue-500 w-full" 
                                                />
                                                <input 
                                                    type="text" 
                                                    placeholder="Телефон"
                                                    value={editRsvpData.phone} 
                                                    onChange={e => setEditRsvpData({...editRsvpData, phone: e.target.value})} 
                                                    className="bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-blue-500 w-full font-mono" 
                                                />
                                            </div>
                                            <div className="flex gap-2 justify-end">
                                                <button 
                                                    onClick={() => handleSaveRsvp(rsvp.id)}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 transition-all shadow-md"
                                                >
                                                    Сохранить
                                                </button>
                                                <button 
                                                    onClick={() => setEditingRsvpId(null)}
                                                    className="px-4 py-2 bg-neutral-850 text-neutral-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:text-white transition-all"
                                                >
                                                    Отмена
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1 min-w-0 mr-4">
                                            <div className="text-base md:text-lg font-black text-white italic uppercase truncate leading-tight">{rsvp.first_name} {rsvp.last_name}</div>
                                            <div className="text-[10px] text-neutral-400 font-bold uppercase tracking-wider mt-0.5">{rsvp.phone || 'Нет телефона'}</div>
                                            <div className="text-[9px] text-neutral-500 font-bold uppercase tracking-widest mt-1 italic truncate">Билет: <span className="text-blue-500">{rsvp.ticket_code}</span></div>
                                        </div>
                                    )}
                                    {editingRsvpId !== rsvp.id && (
                                        <div className="flex gap-2 w-full md:w-auto justify-end">
                                            {['admin', 'superadmin', 'rop', 'partner', 'seller', 'employee', 'staff'].includes(user?.role) && (
                                                <>
                                                    <button 
                                                        onClick={() => {
                                                            setEditingRsvpId(rsvp.id);
                                                            setEditRsvpData({
                                                                first_name: rsvp.first_name || '',
                                                                last_name: rsvp.last_name || '',
                                                                phone: rsvp.phone || ''
                                                            });
                                                        }}
                                                        className="p-2.5 bg-neutral-800 text-neutral-400 rounded-xl hover:text-white transition-all border border-neutral-700 shadow-md"
                                                        title="Редактировать"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteRsvp(rsvp.id)}
                                                        className="p-2.5 bg-rose-500/10 text-rose-500 rounded-xl hover:bg-rose-600 hover:text-white transition-all border border-rose-500/10 shadow-md"
                                                        title="Удалить"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </>
                                            )}
                                            <button 
                                                onClick={async () => { 
                                                    try {
                                                        await client.post(`/events/rsvps/${rsvp.id}/checkin`); 
                                                        viewRSVPs(selectedEvent); 
                                                        toast.success(rsvp.is_attended ? 'Check-in отменен' : 'Check-in выполнен'); 
                                                    } catch {
                                                        toast.error('Ошибка изменения статуса');
                                                    }
                                                }} 
                                                className={`px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg transition-all text-center flex items-center gap-2 min-w-[100px] justify-center ${
                                                    rsvp.is_attended 
                                                    ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 hover:bg-rose-500/20 hover:text-rose-500 hover:border-rose-500/20' 
                                                    : 'bg-blue-600 text-white shadow-blue-600/20 active:scale-95'
                                                }`}
                                            >
                                                {rsvp.is_attended ? (
                                                    <><Check size={14}/> В зале</>
                                                ) : (
                                                    'Check-in'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                             )))}
                        </div>
                        <div className="pt-8 border-t border-white/5 mt-8">
                             <button className="w-full bg-neutral-900 border border-neutral-800 text-neutral-400 py-5 rounded-[2rem] font-bold uppercase text-[9px] tracking-widest flex items-center justify-center gap-2 hover:bg-neutral-800 transition-all">
                                 <Download size={16}/> Скачать CSV реестр
                             </button>
                        </div>
                    </div>
                </div>
            )}

            {isScannerOpen && (
                <BrowserQrScanner 
                    onScan={(code) => { 
                        if(code) { 
                            client.post('/events/check-in', { ticket_code: code }).then(r => { 
                                setScannedParticipant(r.data);
                                refetch(); 
                                setIsScannerOpen(false); 
                            }).catch((err) => toast.error(err.response?.data?.detail || 'Ошибка входа: Код не валиден')); 
                        } 
                    }} 
                    onClose={() => setIsScannerOpen(false)} 
                    title="Check-in Мероприятия" 
                />
            )}

            {/* Scanned Participant Feedback Modal (Card) */}
            {scannedParticipant && (
                <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-300 italic">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-xl rounded-[3rem] p-8 md:p-12 shadow-3xl relative overflow-hidden">
                        <div className={`absolute top-0 left-0 right-0 h-2 shadow-[0_0_20px_rgba(37,99,235,0.5)] ${scannedParticipant.is_regular ? 'bg-amber-500' : 'bg-blue-600'}`} />
                        
                        <div className="flex flex-col md:flex-row gap-8 items-start mb-10">
                            <div className={`w-24 h-24 rounded-[2rem] flex items-center justify-center shrink-0 border shadow-2xl ${scannedParticipant.is_regular ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-blue-600/10 text-blue-500 border-blue-500/20'}`}>
                                <Ticket size={48} />
                            </div>
                            <div className="text-left flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter leading-none">{scannedParticipant.user_name}</h3>
                                    {scannedParticipant.is_regular && (
                                        <span className="bg-amber-500 text-black text-[8px] font-black px-2 py-1 rounded-lg uppercase tracking-widest shadow-lg">Regular</span>
                                    )}
                                </div>
                                <p className="text-xs text-neutral-500 font-black uppercase tracking-widest leading-none">{scannedParticipant.phone || 'NO PHONE'}</p>
                                <div className="flex gap-2 mt-4">
                                    <span className="bg-blue-500/10 text-blue-500 text-[10px] font-black px-3 py-1.5 rounded-full border border-blue-500/20 uppercase tracking-widest">{scannedParticipant.tariff}</span>
                                    <span className="bg-neutral-800 text-neutral-400 text-[10px] font-black px-3 py-1.5 rounded-full border border-neutral-700 uppercase tracking-widest">Участий: {scannedParticipant.participation_count}</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-10">
                             <div className="bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left">
                                 <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Ниша / Бизнес</p>
                                 <p className="text-sm font-bold text-white uppercase italic truncate">
                                    {scannedParticipant.niche || '—'}
                                 </p>
                             </div>
                             <div className="bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left">
                                 <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Город</p>
                                 <p className="text-sm font-bold text-white uppercase italic truncate">
                                    {scannedParticipant.city || '—'}
                                 </p>
                             </div>
                             <div className="col-span-full bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left">
                                 <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Цель участия</p>
                                 <p className="text-sm font-bold text-white italic line-clamp-2">
                                    {scannedParticipant.goal || '—'}
                                 </p>
                             </div>
                        </div>

                        <button 
                            onClick={() => setScannedParticipant(null)} 
                            className="w-full bg-white text-black py-6 rounded-[2rem] font-black uppercase text-xs tracking-[0.2em] shadow-2xl active:scale-95 transition-all"
                        >
                            Готово / Понял
                        </button>
                    </div>
                </div>
            )}
            {/* Media Library Modal */}
            {isLibraryOpen && (
                <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/95 backdrop-blur-2xl" onClick={() => setIsLibraryOpen(false)} />
                    <div className="relative w-full max-w-4xl bg-neutral-900 border border-neutral-800 rounded-[3rem] p-8 md:p-12 shadow-3xl flex flex-col max-h-[85vh] italic overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Библиотека файлов</h2>
                                <p className="text-[9px] text-blue-500 font-bold uppercase tracking-widest mt-1">Выберите файл для {libraryTarget}</p>
                            </div>
                            <button onClick={() => setIsLibraryOpen(false)} className="w-12 h-12 bg-neutral-800 rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={20}/>
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto custom-scrollbar grid grid-cols-2 md:grid-cols-4 gap-4 pr-2">
                            {libraryFiles.length === 0 ? (
                                <div className="col-span-full py-20 text-center opacity-20 flex flex-col items-center gap-4">
                                    <FileText size={48} />
                                    <p className="text-[10px] font-black uppercase tracking-widest">Библиотека пуста</p>
                                </div>
                            ) : (
                                libraryFiles.map(file => (
                                    <div 
                                        key={file.url} 
                                        onClick={() => { setFormData(prev => ({ ...prev, [libraryTarget]: file.url })); setIsLibraryOpen(false); }}
                                        className="group relative aspect-square bg-neutral-950 border border-neutral-800 rounded-2xl overflow-hidden cursor-pointer hover:border-blue-500 transition-all"
                                    >
                                        <img src={file.url} alt={file.filename} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                            <Check size={24} className="text-white" />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Events;
